import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import type { ColorIndicator } from '@disco/utils/color-indicators'
import { deleteItem } from '@disco/utils/immutable'
import { keyEqualTo } from '@disco/utils/keyboard'
import Icon from 'components/ui/icon'
import Tag from 'components/ui/tag'
import styles from './index.module.css'

type TagItem = {
  name: string
  color: ColorIndicator | null | undefined
  value: number | string
}
type Props = {
  autoFocus?: boolean
  tags: TagItem[]
  value: string
  onChange: (values: any[]) => void
  onInputChange: (value: string) => void
}

/**
 * A tags input component that allows adding, removing, and searching through tags
 * @param autoFocus - Whether to automatically focus the input field
 * @param tags - Array of tag objects with name, color, and value properties
 * @param value - The current input value
 * @param onChange - Callback called when the tags array changes
 * @param onInputChange - Callback called when the input value changes
 * @returns A tags input component with search and tag management functionality
 */
const TagsInput = (props: Props): React.ReactElement<'div'> => {
  const inputRef = useRef<HTMLInputElement>(null)
  const sizerRef = useRef(null)
  const placeholder: string =
    props.tags.length > 0
      ? 'Type to search'
      : 'Start typing to find or create tags'
  const [inputWidth, setInputWidth] = useState<string>('auto')
  const focusInput = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [inputRef])
  useEffect(() => {
    if (props.autoFocus) {
      focusInput()
    }
  }, [props.autoFocus])
  useLayoutEffect(() => {
    const width = getNodeWidth(sizerRef)
    setInputWidth(`${Math.max(width)}px`)
  }, [props.tags, sizerRef])
  useLayoutEffect(() => {
    if (inputWidth === 'auto') {
      const width = getNodeWidth(sizerRef)
      setInputWidth(`${Math.max(width)}px`)
    }
  }, [inputWidth, sizerRef])
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputWidth('auto')
      props.onInputChange(event.target.value)
    },
    [props.onInputChange],
  )
  const handleKeyDown = useCallback(
    event => {
      if (
        keyEqualTo(event, 'Delete') &&
        event.target.selectionStart === 0 &&
        event.target.selectionEnd === 0
      ) {
        const values = props.tags.slice(0, -1).map(getTagValue)
        props.onChange(values)
      }
    },
    [props.tags, props.onChange],
  )
  const handleTagRemove = useCallback(
    (tag: TagItem, index: number) => () => {
      const values = deleteItem(props.tags, index).map(getTagValue)
      props.onChange(values)
    },
    [props.tags, props.onChange],
  )
  return (
    <div className={styles.tagsInput} onClick={focusInput}>
      {props.tags.map((tag: TagItem, index: number) => (
        <div className={styles.tagsInputItem} key={index}>
          <Tag
            tag={tag.name}
            tagColor={tag.color}
            onRemoveClick={handleTagRemove(tag, index)}
          />
        </div>
      ))}
      <div className={styles.tagsInputItem}>
        <input
          placeholder={placeholder}
          ref={inputRef}
          style={{
            width: inputWidth,
          }}
          value={props.value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <div className={styles.tagsInputBackground} />
        <div className={styles.tagsInputSizer} ref={sizerRef}>
          <div>{placeholder}</div>
          <div>{props.value}</div>
        </div>
        <div className={styles.tagsInputIcon}>
          <Icon type="search" />
        </div>
      </div>
    </div>
  )
}

const getNodeWidth = ref => {
  if (ref.current) {
    const { width } = ref.current.getBoundingClientRect()
    return width
  }

  return 0
}

const getTagValue = (tag: TagItem): TagItem['value'] => tag.value

export default TagsInput
